// The design system's primitives (front/design-system/components) as typed components. The visuals are the
// originals'; only the typing and a few accessibility details are added.
import React, { useId, useState, type ButtonHTMLAttributes, type CSSProperties, type InputHTMLAttributes, type ReactNode } from "react";

export type ButtonVariant = "primary" | "gradient" | "secondary" | "outline" | "ghost" | "danger";
export type ControlSize = "sm" | "md" | "lg";

const BUTTON_SIZES: Record<ControlSize, { h: string; px: string; fs: string; gap: string }> = {
  sm: { h: "var(--control-sm)", px: "14px", fs: "var(--text-sm)", gap: "6px" },
  md: { h: "var(--control-md)", px: "18px", fs: "var(--text-base)", gap: "8px" },
  lg: { h: "var(--control-lg)", px: "24px", fs: "var(--text-md)", gap: "10px" },
};
const BUTTON_VARIANTS: Record<ButtonVariant, { bg: string; hover: string; fg: string; shadow?: string; border?: string }> = {
  primary: { bg: "var(--action-primary)", hover: "var(--action-primary-hover)", fg: "#fff", shadow: "var(--shadow-brand)" },
  gradient: { bg: "var(--gradient-brand)", hover: "var(--gradient-brand)", fg: "#fff", shadow: "var(--shadow-brand)" },
  secondary: { bg: "var(--action-secondary)", hover: "var(--action-secondary-hover)", fg: "#fff", shadow: "var(--shadow-sm)" },
  outline: { bg: "var(--surface-card)", hover: "var(--surface-hover)", fg: "var(--text-strong)", border: "var(--border-default)", shadow: "var(--shadow-xs)" },
  ghost: { bg: "transparent", hover: "var(--surface-hover)", fg: "var(--text-brand)" },
  danger: { bg: "var(--status-mismatch)", hover: "var(--av-coral-700)", fg: "#fff", shadow: "0 6px 18px rgba(242,95,92,.3)" },
};

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children"> {
  variant?: ButtonVariant;
  size?: ControlSize;
  leadingIcon?: ReactNode;
  trailingIcon?: ReactNode;
  loading?: boolean;
  fullWidth?: boolean;
  children?: ReactNode;
}

export function Button({ variant = "primary", size = "md", leadingIcon, trailingIcon, loading = false, disabled = false, fullWidth = false, children, style, type = "button", ...rest }: ButtonProps) {
  const [hover, setHover] = useState(false);
  const [press, setPress] = useState(false);
  const v = BUTTON_VARIANTS[variant];
  const s = BUTTON_SIZES[size];
  const off = disabled || loading;
  return (
    <button
      {...rest}
      type={type}
      disabled={off}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => { setHover(false); setPress(false); }}
      onMouseDown={() => setPress(true)}
      onMouseUp={() => setPress(false)}
      style={{
        display: fullWidth ? "flex" : "inline-flex", width: fullWidth ? "100%" : undefined, alignItems: "center", justifyContent: "center", gap: s.gap, height: s.h, padding: "0 " + s.px,
        font: "var(--weight-bold) " + s.fs + "/1 var(--font-body)", color: v.fg, background: hover && !off ? v.hover : v.bg, border: v.border ? "1px solid " + v.border : "1px solid transparent",
        borderRadius: "var(--radius-pill)", boxShadow: off ? "none" : v.shadow, cursor: off ? "not-allowed" : "pointer", opacity: disabled ? 0.45 : 1, whiteSpace: "nowrap",
        filter: variant === "gradient" && hover && !off ? "brightness(1.06)" : undefined,
        transform: press && !off ? "scale(0.97)" : hover && !off && variant !== "ghost" ? "translateY(-1px)" : "none",
        transition: "background var(--dur-fast) var(--ease-out),transform var(--dur-fast) var(--ease-out),box-shadow var(--dur-base) var(--ease-out)",
        ...style,
      }}
    >
      {loading ? <span style={{ width: "1em", height: "1em", borderRadius: "50%", border: "2px solid currentColor", borderRightColor: "transparent", animation: "av-spin .7s linear infinite" }} /> : leadingIcon}
      {children}
      {!loading && trailingIcon}
    </button>
  );
}

export type IconButtonVariant = "ghost" | "outline" | "primary" | "inverse";

export function IconButton({ icon, label, variant = "ghost", size = "md", disabled = false, style, type = "button", ...rest }: { icon: ReactNode; label: string; variant?: IconButtonVariant; size?: ControlSize } & ButtonHTMLAttributes<HTMLButtonElement>) {
  const [hover, setHover] = useState(false);
  const d = { sm: 32, md: 40, lg: 48 }[size];
  const map: Record<IconButtonVariant, [string, string, string, string]> = {
    ghost: ["transparent", "var(--surface-hover)", "var(--text-body)", "transparent"],
    outline: ["var(--surface-card)", "var(--surface-hover)", "var(--text-strong)", "var(--border-default)"],
    primary: ["var(--action-primary)", "var(--action-primary-hover)", "#fff", "transparent"],
    inverse: ["rgba(255,255,255,.08)", "rgba(255,255,255,.16)", "#fff", "transparent"],
  };
  const [bg, hb, fg, bd] = map[variant];
  return (
    <button
      {...rest}
      type={type}
      aria-label={label}
      title={label}
      disabled={disabled}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{ width: d, height: d, display: "inline-flex", alignItems: "center", justifyContent: "center", borderRadius: "var(--radius-pill)", border: "1px solid " + bd, background: hover && !disabled ? hb : bg, color: hover && variant === "ghost" ? "var(--text-brand)" : fg, cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.45 : 1, transition: "background var(--dur-fast) var(--ease-out),color var(--dur-fast)", padding: 0, ...style }}
    >
      {icon}
    </button>
  );
}

export type BadgeTone = "matched" | "review" | "mismatch" | "pending" | "neutral" | "brand";
const BADGE_TONES: Record<BadgeTone, [string, string, string]> = {
  matched: ["--status-matched-bg", "--status-matched-fg", "--status-matched"],
  review: ["--status-review-bg", "--status-review-fg", "--status-review"],
  mismatch: ["--status-mismatch-bg", "--status-mismatch-fg", "--status-mismatch"],
  pending: ["--status-pending-bg", "--status-pending-fg", "--status-pending"],
  neutral: ["--status-neutral-bg", "--status-neutral-fg", "--av-gray-400"],
  brand: ["--av-blue-500", "--av-white", "--av-white"],
};

export function Badge({ tone = "neutral", dot = true, size = "md", children, style }: { tone?: BadgeTone; dot?: boolean; size?: "sm" | "md"; children?: ReactNode; style?: CSSProperties }) {
  const [bg, fg, d] = BADGE_TONES[tone];
  const sm = size === "sm";
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, height: sm ? 20 : 24, padding: sm ? "0 8px" : "0 10px", borderRadius: "var(--radius-pill)", background: "var(" + bg + ")", color: "var(" + fg + ")", font: "var(--weight-bold) " + (sm ? "var(--text-2xs)" : "var(--text-xs)") + "/1 var(--font-body)", whiteSpace: "nowrap", ...style }}>
      {dot && <span style={{ width: 6, height: 6, borderRadius: "50%", background: "var(" + d + ")" }} />}
      {children}
    </span>
  );
}

export type CardVariant = "default" | "sunken" | "inverse" | "brand" | "glass";
export type CardPadding = "none" | "sm" | "md" | "lg" | "xl";

export function Card({ variant = "default", padding = "lg", interactive = false, children, style, ...rest }: { variant?: CardVariant; padding?: CardPadding; interactive?: boolean; children?: ReactNode } & React.HTMLAttributes<HTMLDivElement>) {
  const [hover, setHover] = useState(false);
  const pad = { none: 0, sm: "var(--space-4)", md: "var(--space-5)", lg: "var(--space-6)", xl: "var(--space-8)" }[padding];
  const v: Record<CardVariant, CSSProperties> = {
    default: { background: "var(--surface-card)", border: "1px solid var(--border-subtle)", boxShadow: "var(--shadow-sm)", color: "var(--text-body)" },
    sunken: { background: "var(--surface-sunken)", border: "1px solid transparent", color: "var(--text-body)" },
    inverse: { background: "var(--gradient-glow),var(--gradient-deep)", border: "1px solid transparent", color: "var(--text-inverse)", boxShadow: "var(--shadow-md)" },
    brand: { background: "var(--gradient-brand)", border: "1px solid transparent", color: "#fff", boxShadow: "var(--shadow-brand)" },
    glass: { background: "var(--surface-glass)", backdropFilter: "var(--blur-glass)", border: "1px solid rgba(255,255,255,.6)", boxShadow: "var(--shadow-md)" },
  };
  const base = v[variant];
  return (
    <div
      {...rest}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{ borderRadius: "var(--radius-xl)", padding: pad, ...base, ...(interactive ? { cursor: "pointer", transform: hover ? "translateY(-2px)" : "none", boxShadow: hover ? "var(--shadow-lg)" : base.boxShadow, transition: "transform var(--dur-base) var(--ease-out),box-shadow var(--dur-base) var(--ease-out)" } : {}), ...style }}
    >
      {children}
    </div>
  );
}

export interface TabItem { value: string; label: ReactNode; count?: number }

export function Tabs({ items, value, onChange, variant = "pill", style }: { items: (TabItem | string)[]; value: string; onChange?: (value: string) => void; variant?: "pill" | "underline"; style?: CSSProperties }) {
  const pill = variant === "pill";
  return (
    <div role="tablist" style={{ display: "inline-flex", gap: pill ? 4 : 24, padding: pill ? 4 : 0, background: pill ? "var(--surface-sunken)" : "transparent", borderRadius: pill ? "var(--radius-pill)" : 0, borderBottom: pill ? "none" : "1px solid var(--border-subtle)", ...style }}>
      {items.map((it) => {
        const item: TabItem = typeof it === "string" ? { value: it, label: it } : it;
        const on = item.value === value;
        return (
          <button
            key={item.value}
            type="button"
            role="tab"
            aria-selected={on}
            onClick={() => onChange?.(item.value)}
            style={{
              display: "inline-flex", alignItems: "center", gap: 8, height: pill ? 34 : 42, padding: pill ? "0 16px" : "0 2px", border: 0, cursor: "pointer",
              borderRadius: pill ? "var(--radius-pill)" : 0, background: pill && on ? "var(--surface-card)" : "transparent", boxShadow: pill && on ? "var(--shadow-sm)" : "none",
              borderBottom: pill ? "none" : "2.5px solid " + (on ? "var(--action-primary)" : "transparent"), marginBottom: pill ? 0 : -1,
              color: on ? "var(--text-strong)" : "var(--text-muted)", font: "var(--weight-bold) var(--text-sm)/1 var(--font-body)", transition: "all var(--dur-base) var(--ease-out)",
            }}
          >
            {item.label}
            {item.count != null && <span style={{ minWidth: 20, height: 20, padding: "0 6px", borderRadius: 99, display: "inline-flex", alignItems: "center", justifyContent: "center", background: on ? "var(--action-primary)" : "var(--av-gray-200)", color: on ? "#fff" : "var(--text-body)", font: "var(--weight-extrabold) var(--text-2xs)/1 var(--font-body)" }}>{item.count}</span>}
          </button>
        );
      })}
    </div>
  );
}

export function Tag({ children, icon, onRemove, selected = false, onClick, style }: { children?: ReactNode; icon?: ReactNode; onRemove?: () => void; selected?: boolean; onClick?: () => void; style?: CSSProperties }) {
  const [hover, setHover] = useState(false);
  return (
    <span onClick={onClick} onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)} style={{ display: "inline-flex", alignItems: "center", gap: 6, height: 30, padding: onRemove ? "0 6px 0 12px" : "0 12px", borderRadius: "var(--radius-pill)", background: selected ? "var(--av-blue-50)" : hover && onClick ? "var(--surface-sunken)" : "var(--surface-card)", border: "1.5px solid " + (selected ? "var(--av-blue-300)" : "var(--border-default)"), color: selected ? "var(--av-blue-700)" : "var(--text-body)", font: "var(--weight-semibold) var(--text-sm)/1 var(--font-body)", cursor: onClick ? "pointer" : "default", transition: "background var(--dur-fast)", ...style }}>
      {icon}
      {children}
      {onRemove && (
        <button type="button" onClick={(e) => { e.stopPropagation(); onRemove(); }} aria-label="Remove" style={{ width: 20, height: 20, display: "flex", alignItems: "center", justifyContent: "center", border: 0, borderRadius: "50%", background: "transparent", color: "inherit", cursor: "pointer", padding: 0 }}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.75" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
        </button>
      )}
    </span>
  );
}

export function Dialog({ open = true, title, description, children, footer, onClose, width = 480, inline = false }: { open?: boolean; title?: ReactNode; description?: ReactNode; children?: ReactNode; footer?: ReactNode; onClose?: () => void; width?: number; inline?: boolean }) {
  React.useEffect(() => {
    if (!open || !onClose || inline) return;
    const key = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", key);
    return () => window.removeEventListener("keydown", key);
  }, [open, onClose, inline]);
  if (!open) return null;
  const panel = (
    <div role="dialog" aria-modal="true" style={{ width: "100%", maxWidth: width, background: "var(--surface-card)", borderRadius: "var(--radius-2xl)", boxShadow: "var(--shadow-lg)", padding: "var(--space-8)", display: "flex", flexDirection: "column", gap: "var(--space-5)", animation: "av-rise var(--dur-slow) var(--ease-out)", maxHeight: "calc(100vh - 48px)", overflow: "auto" }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 16 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {title && <h3 style={{ font: "var(--type-h3)", fontSize: "var(--text-xl)", color: "var(--text-strong)", margin: 0 }}>{title}</h3>}
          {description && <p style={{ margin: 0, font: "var(--type-body)", color: "var(--text-muted)" }}>{description}</p>}
        </div>
        {onClose && (
          <button type="button" onClick={onClose} aria-label="Close" style={{ flex: "none", width: 32, height: 32, borderRadius: "50%", border: 0, background: "var(--surface-sunken)", color: "var(--text-muted)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
          </button>
        )}
      </div>
      {children}
      {footer && <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, paddingTop: 4, flexWrap: "wrap" }}>{footer}</div>}
    </div>
  );
  if (inline) return panel;
  return (
    <div onClick={(e) => { if (e.target === e.currentTarget && onClose) onClose(); }} style={{ position: "fixed", inset: 0, zIndex: 100, background: "var(--surface-overlay)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      {panel}
    </div>
  );
}

export function Progress({ value = 0, max = 100, label, showValue = true, tone = "brand", size = "md", style }: { value?: number; max?: number; label?: ReactNode; showValue?: boolean; tone?: "brand" | "fresh" | "review" | "mismatch"; size?: "sm" | "md"; style?: CSSProperties }) {
  const pct = max > 0 ? Math.max(0, Math.min(100, (value / max) * 100)) : 0;
  const bg = { brand: "var(--gradient-brand)", fresh: "var(--gradient-fresh)", review: "var(--status-review)", mismatch: "var(--status-mismatch)" }[tone];
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8, ...style }}>
      {(label || showValue) && <div style={{ display: "flex", justifyContent: "space-between", font: "var(--type-label)", color: "var(--text-strong)" }}><span>{label}</span>{showValue && <span style={{ fontFamily: "var(--font-mono)", fontWeight: 500, color: "var(--text-muted)" }}>{Math.round(pct)}%</span>}</div>}
      <div role="progressbar" aria-valuenow={value} aria-valuemax={max} style={{ height: size === "sm" ? 6 : 10, borderRadius: 99, background: "var(--av-gray-100)", overflow: "hidden" }}>
        <div style={{ width: pct + "%", height: "100%", borderRadius: 99, background: bg, transition: "width var(--dur-slow) var(--ease-out)" }} />
      </div>
    </div>
  );
}

export type ToastTone = "success" | "info" | "warning" | "error";
const TOAST_TONES: Record<ToastTone, [string, string]> = {
  success: ["var(--status-matched)", "M20 6 9 17l-5-5"],
  info: ["var(--status-pending)", "M12 16v-4M12 8h.01"],
  warning: ["var(--status-review)", "M12 9v4M12 17h.01"],
  error: ["var(--status-mismatch)", "M18 6 6 18M6 6l12 12"],
};

export function Toast({ tone = "success", title, message, action, onClose, style }: { tone?: ToastTone; title?: ReactNode; message?: ReactNode; action?: ReactNode; onClose?: () => void; style?: CSSProperties }) {
  const [c, p] = TOAST_TONES[tone];
  return (
    <div role="status" style={{ display: "flex", alignItems: "center", gap: 14, minWidth: 320, maxWidth: 440, padding: "14px 16px 14px 14px", background: "var(--surface-inverse)", color: "#fff", borderRadius: "var(--radius-lg)", boxShadow: "var(--shadow-lg)", animation: "av-rise var(--dur-slow) var(--ease-spring)", ...style }}>
      <span style={{ flex: "none", width: 32, height: 32, borderRadius: "50%", background: c, display: "flex", alignItems: "center", justifyContent: "center", color: tone === "warning" ? "var(--av-navy-900)" : "#fff" }}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d={p} /></svg>
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        {title && <div style={{ font: "var(--weight-extrabold) var(--text-base)/1.3 var(--font-display)" }}>{title}</div>}
        {message && <div style={{ font: "var(--type-body-sm)", color: "var(--av-sky)" }}>{message}</div>}
      </div>
      {action}
      {onClose && (
        <button type="button" onClick={onClose} aria-label="Dismiss" style={{ border: 0, background: "transparent", color: "var(--av-sky)", cursor: "pointer", padding: 4, display: "flex" }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
        </button>
      )}
    </div>
  );
}

export function Tooltip({ content, children, placement = "top" }: { content: ReactNode; children: ReactNode; placement?: "top" | "bottom" | "left" | "right" }) {
  const [open, setOpen] = useState(false);
  const pos: Record<string, CSSProperties> = {
    top: { bottom: "calc(100% + 8px)", left: "50%", transform: "translateX(-50%)" },
    bottom: { top: "calc(100% + 8px)", left: "50%", transform: "translateX(-50%)" },
    right: { left: "calc(100% + 8px)", top: "50%", transform: "translateY(-50%)" },
    left: { right: "calc(100% + 8px)", top: "50%", transform: "translateY(-50%)" },
  };
  return (
    <span onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)} onFocus={() => setOpen(true)} onBlur={() => setOpen(false)} style={{ position: "relative", display: "inline-flex" }}>
      {children}
      {open && <span role="tooltip" style={{ position: "absolute", ...pos[placement], zIndex: 50, whiteSpace: "nowrap", padding: "7px 10px", borderRadius: "var(--radius-sm)", background: "var(--surface-inverse)", color: "#fff", font: "var(--weight-semibold) var(--text-xs)/1.3 var(--font-body)", boxShadow: "var(--shadow-md)", pointerEvents: "none" }}>{content}</span>}
    </span>
  );
}

export function Checkbox({ checked = false, indeterminate = false, onChange, label, description, disabled = false, style }: { checked?: boolean; indeterminate?: boolean; onChange?: (checked: boolean) => void; label?: ReactNode; description?: ReactNode; disabled?: boolean; style?: CSSProperties }) {
  const on = checked || indeterminate;
  return (
    <label style={{ display: "inline-flex", alignItems: "flex-start", gap: 10, cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.5 : 1, ...style }}>
      <input type="checkbox" checked={checked} disabled={disabled} onChange={(e) => onChange?.(e.target.checked)} style={{ position: "absolute", opacity: 0, width: 0, height: 0 }} />
      <span style={{ flex: "none", width: 20, height: 20, marginTop: 1, borderRadius: 6, border: on ? "0" : "1.5px solid var(--border-strong)", background: on ? "var(--action-primary)" : "var(--surface-card)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", transition: "background var(--dur-fast) var(--ease-out)", boxShadow: on ? "0 2px 6px rgba(0,131,192,.3)" : "var(--shadow-inset)" }}>
        {checked && !indeterminate && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.25" strokeLinecap="round" strokeLinejoin="round" style={{ animation: "av-pop var(--dur-slow) var(--ease-spring)" }}><path d="M20 6 9 17l-5-5" /></svg>}
        {indeterminate && <span style={{ width: 10, height: 2.5, borderRadius: 2, background: "#fff" }} />}
      </span>
      {(label || description) && (
        <span style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {label && <span style={{ font: "var(--weight-semibold) var(--text-base)/1.35 var(--font-body)", color: "var(--text-strong)" }}>{label}</span>}
          {description && <span style={{ font: "var(--type-body-sm)", color: "var(--text-muted)" }}>{description}</span>}
        </span>
      )}
    </label>
  );
}

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "size"> {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  leadingIcon?: ReactNode;
  trailingAddon?: ReactNode;
  size?: ControlSize;
  mono?: boolean;
  containerStyle?: CSSProperties;
}

export function Input({ label, hint, error, leadingIcon, trailingAddon, size = "md", mono = false, disabled = false, id, containerStyle, style, ...rest }: InputProps) {
  const [focus, setFocus] = useState(false);
  const generated = useId();
  const iid = id || generated;
  const h = size === "sm" ? "var(--control-sm)" : size === "lg" ? "var(--control-lg)" : "var(--control-md)";
  const bc = error ? "var(--status-mismatch)" : focus ? "var(--border-focus)" : "var(--border-default)";
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6, ...containerStyle }}>
      {label && <label htmlFor={iid} style={{ font: "var(--type-label)", color: "var(--text-strong)" }}>{label}</label>}
      <div style={{ display: "flex", alignItems: "center", gap: 8, height: h, padding: "0 14px", background: disabled ? "var(--surface-sunken)" : "var(--surface-card)", border: "1.5px solid " + bc, borderRadius: "var(--radius-md)", boxShadow: focus ? (error ? "0 0 0 3px rgba(242,95,92,.2)" : "var(--shadow-focus)") : "var(--shadow-xs)", transition: "border-color var(--dur-fast),box-shadow var(--dur-base) var(--ease-out)" }}>
        {leadingIcon && <span style={{ color: "var(--text-subtle)", display: "flex", font: "var(--type-body-sm)" }}>{leadingIcon}</span>}
        <input id={iid} disabled={disabled} onFocus={() => setFocus(true)} onBlur={() => setFocus(false)} {...rest} style={{ flex: 1, minWidth: 0, border: 0, outline: 0, background: "transparent", font: mono ? "var(--type-figure)" : "var(--type-body)", color: "var(--text-strong)", ...style }} />
        {trailingAddon && <span style={{ color: "var(--text-muted)", font: "var(--type-body-sm)" }}>{trailingAddon}</span>}
      </div>
      {(error || hint) && <div style={{ font: "var(--type-body-sm)", color: error ? "var(--status-mismatch-fg)" : "var(--text-muted)" }}>{error || hint}</div>}
    </div>
  );
}

export function Textarea({ label, value, onChange, placeholder, required, rows = 3, hint, error }: { label?: ReactNode; value: string; onChange: (value: string) => void; placeholder?: string; required?: boolean; rows?: number; hint?: ReactNode; error?: ReactNode }) {
  const [focus, setFocus] = useState(false);
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {label && <span style={{ font: "var(--type-label)", color: "var(--text-strong)" }}>{label}{required ? "" : <span style={{ fontWeight: 400, color: "var(--text-muted)" }}> (optional)</span>}</span>}
      <textarea value={value} onChange={(e) => onChange(e.target.value)} onFocus={() => setFocus(true)} onBlur={() => setFocus(false)} placeholder={placeholder} rows={rows} style={{ resize: "vertical", padding: "10px 14px", border: "1.5px solid " + (error ? "var(--status-mismatch)" : focus ? "var(--border-focus)" : "var(--border-default)"), borderRadius: "var(--radius-md)", boxShadow: focus ? "var(--shadow-focus)" : "var(--shadow-xs)", outline: 0, font: "var(--type-body)", color: "var(--text-strong)", background: "var(--surface-card)" }} />
      {(error || hint) && <span style={{ font: "var(--type-body-sm)", color: error ? "var(--status-mismatch-fg)" : "var(--text-muted)" }}>{error || hint}</span>}
    </label>
  );
}

export interface SelectOption { value: string; label: string }

export function Select({ label, options, value, onChange, placeholder, size = "md", disabled = false, id, style }: { label?: ReactNode; options: (SelectOption | string)[]; value: string; onChange?: (value: string) => void; placeholder?: string; size?: ControlSize; disabled?: boolean; id?: string; style?: CSSProperties }) {
  const [focus, setFocus] = useState(false);
  const generated = useId();
  const iid = id || generated;
  const h = size === "sm" ? "var(--control-sm)" : size === "lg" ? "var(--control-lg)" : "var(--control-md)";
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6, ...style }}>
      {label && <label htmlFor={iid} style={{ font: "var(--type-label)", color: "var(--text-strong)" }}>{label}</label>}
      <div style={{ position: "relative" }}>
        <select id={iid} value={value} disabled={disabled} onChange={(e) => onChange?.(e.target.value)} onFocus={() => setFocus(true)} onBlur={() => setFocus(false)} style={{ appearance: "none", WebkitAppearance: "none", width: "100%", height: h, padding: "0 40px 0 14px", font: size === "sm" ? "var(--type-body-sm)" : "var(--type-body)", color: value ? "var(--text-strong)" : "var(--text-subtle)", background: disabled ? "var(--surface-sunken)" : "var(--surface-card)", border: "1.5px solid " + (focus ? "var(--border-focus)" : "var(--border-default)"), borderRadius: "var(--radius-md)", boxShadow: focus ? "var(--shadow-focus)" : "var(--shadow-xs)", outline: 0, cursor: "pointer" }}>
          {placeholder && <option value="">{placeholder}</option>}
          {options.map((o) => { const opt = typeof o === "string" ? { value: o, label: o } : o; return <option key={opt.value} value={opt.value}>{opt.label}</option>; })}
        </select>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" style={{ position: "absolute", right: 14, top: "50%", transform: "translateY(-50%)", pointerEvents: "none", color: "var(--text-muted)" }}><path d="m6 9 6 6 6-6" /></svg>
      </div>
    </div>
  );
}

export function Radio({ checked = false, onChange, label, description, name, value, disabled = false, style }: { checked?: boolean; onChange?: (value: string) => void; label?: ReactNode; description?: ReactNode; name?: string; value: string; disabled?: boolean; style?: CSSProperties }) {
  return (
    <label style={{ display: "inline-flex", alignItems: "flex-start", gap: 10, cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.5 : 1, ...style }}>
      <input type="radio" name={name} value={value} checked={checked} disabled={disabled} onChange={() => onChange?.(value)} style={{ position: "absolute", opacity: 0, width: 0, height: 0 }} />
      <span style={{ flex: "none", width: 20, height: 20, marginTop: 1, borderRadius: "50%", border: checked ? "6px solid var(--action-primary)" : "1.5px solid var(--border-strong)", background: "var(--surface-card)", transition: "border var(--dur-base) var(--ease-spring)" }} />
      {(label || description) && (
        <span style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {label && <span style={{ font: "var(--weight-semibold) var(--text-base)/1.35 var(--font-body)", color: "var(--text-strong)" }}>{label}</span>}
          {description && <span style={{ font: "var(--type-body-sm)", color: "var(--text-muted)" }}>{description}</span>}
        </span>
      )}
    </label>
  );
}

export function Switch({ checked = false, onChange, label, size = "md", disabled = false, style }: { checked?: boolean; onChange?: (checked: boolean) => void; label?: ReactNode; size?: "sm" | "md"; disabled?: boolean; style?: CSSProperties }) {
  const w = size === "sm" ? 32 : 42, h = size === "sm" ? 18 : 24, k = h - 6;
  return (
    <label style={{ display: "inline-flex", alignItems: "center", gap: 10, cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.5 : 1, ...style }}>
      <button type="button" role="switch" aria-checked={checked} disabled={disabled} onClick={() => onChange?.(!checked)} style={{ position: "relative", width: w, height: h, flex: "none", padding: 0, border: 0, borderRadius: 99, background: checked ? "var(--gradient-fresh)" : "var(--av-gray-200)", cursor: "inherit", transition: "background var(--dur-base)" }}>
        <span style={{ position: "absolute", top: 3, left: checked ? w - k - 3 : 3, width: k, height: k, borderRadius: "50%", background: "#fff", boxShadow: "0 1px 3px rgba(14,32,55,.25)", transition: "left var(--dur-slow) var(--ease-spring)" }} />
      </button>
      {label && <span style={{ font: "var(--weight-semibold) var(--text-base)/1.35 var(--font-body)", color: "var(--text-strong)" }}>{label}</span>}
    </label>
  );
}
